import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './home.css'
import { RhyBusPageHeader } from '@rhybus/header'
import { RhyBusFilters } from '@rhybus/filters'
import { RhyBusCard } from '@rhybus/card'
import { RhyBusButton } from '@rhybus/button'
import { RhyBusDashboardkpis } from '@rhybus/dashboard-kpis'
import { RhyBusCharts } from '@rhybus/charts'
import { RhyBusCalendarDashboard } from '@rhybus/calendar'
import { RhyBusQuickLinksDashBoard } from '@rhybus/quicklinks'
import { getQuickLinksData } from '../../services/quicklinks'
import { fetchHomeCaseItems, fetchTrainingItemsForKpi } from '../../services/listview'
import PageOverlay from '../../utils/controls/pageoverLay/pageoverLay'
import { RhyBusPointofContactDashBoard } from '@rhybus/point-of-contact'
import { RhyBusNoresult } from '@rhybus/noresult'
import { RhyBusBadge } from '@rhybus/badge'
import { useGetCalendarQuery, useGetPOCQuery } from '../../store/settings/hooks'
import { homeCalendarCategories } from '../../config/configuration'
import {
	buildHomeNewFilterConfig,
	buildHomeNewInitialFilters,
	HomeNewFilterState,
	filterHomeNewRequirements,
	HOME_NEW_DISPLAY,
	HOME_NEW_FUNDING_STATUS_DATA,
	HOME_NEW_FUNDING_STATUS_PIE_CONFIG,
	HOME_NEW_GRANT_UTILIZATION_CONFIG,
	HOME_NEW_POC_DATA,
	HOME_NEW_MONTHLY_CALENDAR,
	HOME_NEW_PRIORITY_DATA,
	HOME_NEW_PRIORITY_DONUT_CONFIG,
	HOME_NEW_TASK_STATUS_DATA,
	HOME_NEW_TASK_STATUS_DONUT_CONFIG,
	HOME_NEW_TMASL_BAR_CONFIG,
	dashboardKpiData
} from '../../config/configuration'
import { title } from 'process'

const renderLegend = (items: readonly { name: string; value: number; fill: string }[]) => (
	<ul className='home-donut-legend'>
		{items.map((item) => (
			<li key={item.name} className='home-donut-legend-item'>
				<span className='home-donut-legend-swatch' style={{ backgroundColor: item.fill }} aria-hidden='true' />
				<span className='home-donut-legend-label inter-medium font-12'>{item.name}</span>
				<span className='home-donut-legend-value inter-semibold font-12'>({item.value})</span>
			</li>
		))}
	</ul>
)

const PROGRAM_TYPE_COLORS = ['#8722E4', '#3148C6', '#6874A6', '#237F92', '#7A1FE5', '#807605', '#963411', '#2F1DBA', '#115696', '#5FA91F']

const renderPanelHeader = (title: string, count?: number) => (
	<div className='d-flex align-items-center gap-2 inter-semibold font-18' tabIndex={0} aria-label={`${title} ${count}`}>
		{title}
		{typeof count === 'number' ? <RhyBusBadge inlineType='text' count={count} inline /> : null}
	</div>
)

export default function HomeNew() {
	const navigate = useNavigate()
	const { data: calendarData = [] } = useGetCalendarQuery()
	const { data: pocRaw = [] } = useGetPOCQuery()
	const pocData = useMemo(() =>
		pocRaw
			.filter((item: any) => !!(item?.PointOfContact || item?.ContactName))
			.map((item: any) => ({
				...item,
				ContactName: item.PointOfContact || item.ContactName || '',
				ContactTitle: '',
				ContactEmail: item.Title || item.ContactEmail || '',
				ContactPhone: item.PhoneNumber || item.ContactPhone || '',
				IsArchived: item.IsArchived === true || item.IsArchived === 'Yes',
			}))
	, [pocRaw])
	const [quickLinksItems, setQuickLinksItems] = useState<any[]>([])
	const [allCaseItems, setAllCaseItems] = useState<any[]>([])
	const [allTrainingItems, setAllTrainingItems] = useState<{ caseGUID: string; statusCode: number }[]>([])
	const [loading, setLoading] = useState(true)
	const [searchText, setSearchText] = useState('')
	const [filters, setFilters] = useState<HomeNewFilterState>({})
	const [activeChartFilters, setActiveChartFilters] = useState<Record<string, boolean>>({})

	useEffect(() => {
		setLoading(true)
		Promise.all([
			getQuickLinksData().then((data) => setQuickLinksItems(data ?? [])),
			fetchHomeCaseItems().then((items) => {
				const safeItems = items ?? []
				setAllCaseItems(safeItems)
				const config = buildHomeNewFilterConfig(safeItems)
				setFilters(buildHomeNewInitialFilters(config))
			}),
			fetchTrainingItemsForKpi().then((data) => setAllTrainingItems(data ?? [])),
		]).finally(() => setLoading(false))
	}, [])

	const filterConfig = useMemo(() => buildHomeNewFilterConfig(allCaseItems), [allCaseItems])

	const filteredData = useMemo(() => {
		return filterHomeNewRequirements(allCaseItems, filters, searchText)
	}, [allCaseItems, filters, searchText])

	const dashboardCalendarEvents = useMemo(() => {
		return (calendarData ?? []).map((item: any) => ({
			...item,
			title: item.title ?? item.Title ?? '',
			Title: item.Title ?? item.title ?? '',
			EventDate: item.EventDate ?? item.StartDate ?? item.start ?? null,
			FreeBusy: item.FreeBusy ?? item.RRule ?? null,
			fAllDayEvent: item.fAllDayEvent ?? item.IsAllDayEvent ?? false,
			fRecurrence: item.fRecurrence ?? item.Recurrence ?? false,
		}))
	}, [calendarData])

	const renderChartBody = (content: ReactNode) => {
		return filteredData.length === 0 ? <RhyBusNoresult title='There are no results to display' /> : content
	}

	const handleFilterChange = (_key: string, _value: unknown, nextFilters: Record<string, { value?: Array<{ label: string; value: string }> }>) => {
		if (nextFilters == null) return
		const safeFilters: HomeNewFilterState = {}
		Object.entries(nextFilters).forEach(([k, v]) => {
			if (v == null) return
			safeFilters[k] = {
				order: (v as any).order ?? 0,
				value: Array.isArray(v.value) ? v.value.filter((item) => item != null && item.value != null) : [],
			}
		})
		setFilters(safeFilters)
	}

	const handleSearch = (value: string) => {
		setSearchText(value)
	}

	const applyFilterFromChart = useCallback((filterKey: string, filterValue: string) => {
		if (!filterValue) return
		setFilters((prev) => ({
			...prev,
			[filterKey]: {
				...(prev[filterKey] ?? { order: 0 }),
				value: [{ label: filterValue, value: filterValue }],
			},
		}))
		setActiveChartFilters((prev) => ({ ...prev, [filterKey]: true }))
	}, [])

	const handleClearAll = () => {
		setFilters(buildHomeNewInitialFilters(filterConfig))
		setSearchText('')
		setActiveChartFilters({})
	}

	const handleViewAll = useCallback((_chartFilterKey: string) => {
		const activeFilters: Record<string, { order: number; value: { label: string; value: string }[] }> = {}
		Object.entries(filters).forEach(([key, val]) => {
			const firstValue = val?.value?.[0]?.value
			if (firstValue && firstValue !== 'All') {
				activeFilters[key] = { order: val.order ?? 0, value: val.value }
			}
		})
		if (_chartFilterKey === 'country') {
        	activeFilters.country = {
            	order: 0,
            	value: countryChartData.map(c => ({
                	label: c.name,
                	value: c.name
            	}))
        	}
    	}
    	navigate('/cases', { state: { initialFilters: activeFilters } })
	}, [filters, navigate])

	const handleKpiClick = useCallback((status: string) => {
		const kpiRouteMap: Record<string, string> = {
			'Total Trainings': '/trainings',
			'Total Cases': '/cases',
			'Active Trainings': '/trainings',
			'Completed Trainings': '/trainings',
		}
		const route = kpiRouteMap[status]
		if (route) {
			navigate(route)
		}
	}, [navigate])

	const filteredCaseGuids = useMemo(
		() => new Set(filteredData.map((item: any) => item.ItemGuid).filter((id: any) => id != null)),
		[filteredData]
	)

	const filteredTrainings = useMemo(
		() => allTrainingItems.filter(t => filteredCaseGuids.has(t.caseGUID)),
		[allTrainingItems, filteredCaseGuids]
	)

	const kpisWithCounts = useMemo(() => {
		const kpiValueMap: Record<string, number> = {
			'Total Cases': filteredData.length,
			'Total Trainings': allTrainingItems.filter(t => t.statusCode !== 3).length,
			'Active Trainings': allTrainingItems.filter(t => t.statusCode !== 3).length,
			'Completed Trainings': filteredTrainings.filter(t => t.statusCode === 8).length,
		}
		return dashboardKpiData.map((item) => ({
			...item,
			Title: item.Title,
			value: kpiValueMap[item.Title] ?? item.value,
			setActiveKPI: handleKpiClick,
		}))
	}, [filteredData, filteredCaseGuids, allTrainingItems, handleKpiClick])

	const TRAINING_STATUS_LABEL: Record<number, string> = {
		2: 'Submitted',
		3: 'Canceled',
		4: 'Seat Validation',
		5: 'Execution',
		6: 'Utilization',
		7: 'Billing',
		8: 'Reconciliation',
		9: 'Closure',
	}

	const trainingStatusChartData = useMemo(() => {
		const counts: Record<number, number> = {}
		filteredTrainings.forEach(t => {
			if (TRAINING_STATUS_LABEL[t.statusCode]) {
				counts[t.statusCode] = (counts[t.statusCode] || 0) + 1
			}
		})
		return Object.entries(counts)
			.sort(([a], [b]) => Number(a) - Number(b))
			.map(([code, value]) => ({ name: TRAINING_STATUS_LABEL[Number(code)], value }))
	}, [filteredTrainings])

	const trainingStatusBarChartConfig = useMemo(() => ({
		chartType: 'bar',
		dataKey: 'name',
		container_width: '100%',
		container_height: 220,
		fill: true,
		stroke: false,
		ariaLabel: 'By Training Status',
		title: 'By Training Status',
		barSize: 22,
		data: trainingStatusChartData,
		series: [{ dataKey: 'value', color: '#237F92' }],
		cartesianGrid: { stroke: '#E7EAF3', vertical: false },
		xAxis: { axisLine: false, tickLine: false, interval: 0, tickMargin: 10, height: 40 },
		yAxis: { axisLine: { stroke: '#CBD5E1' }, tickLine: false, width: 52, label: { value: 'Trainings', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } } },
		legend: { legend: false },
	}), [trainingStatusChartData])

	const countryChartData = useMemo(() => {
		const counts: Record<string, number> = {}
		filteredData.forEach((item) => { if (item.country) counts[item.country] = (counts[item.country] || 0) + 1 })
		return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b)).map(([name, value]) => ({ name, value }))
	}, [filteredData])

	const impAgencyChartData = useMemo(() => {
		const counts: Record<string, number> = {}
		filteredData.forEach((item) => { if (item.ia) counts[item.ia] = (counts[item.ia] || 0) + 1 })
		return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b)).map(([name, value]) => ({ name, value }))
	}, [filteredData])

	const programTypeChartData = useMemo(() => {
		const counts: Record<string, number> = {}
		filteredData.forEach((item) => { if (item.programtype) counts[item.programtype] = (counts[item.programtype] || 0) + 1 })
		return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b)).map(([name, value], i) => ({ name, value, fill: PROGRAM_TYPE_COLORS[i % PROGRAM_TYPE_COLORS.length] }))
	}, [filteredData])

	const countryBarChartConfig = useMemo(() => ({
		chartType: 'bar',
		dataKey: 'name',
		container_width: '100%',
		container_height: 220,
		fill: true,
		stroke: false,
		ariaLabel: 'By Country',
		title: 'By Country',
		barSize: 22,
		data: countryChartData,
		series: [{ dataKey: 'value', color: '#237F92' }],
		cartesianGrid: { stroke: '#E7EAF3', vertical: false },
		xAxis: { axisLine: false, tickLine: false, interval: 0, tickMargin: 10, height: 40 },
		yAxis: { axisLine: { stroke: '#CBD5E1' }, tickLine: false, width: 52, label: { value: 'Cases', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } } },
		legend: { legend: false },
		handleClick: (e: any) => { const clicked = e?.name; if (clicked) applyFilterFromChart('country', clicked) },
	}), [countryChartData, applyFilterFromChart])

	const impAgencyBarChartConfig = useMemo(() => ({
		chartType: 'bar',
		dataKey: 'name',
		container_width: '100%',
		container_height: 220,
		fill: true,
		stroke: false,
		ariaLabel: 'By Implementing Agency',
		title: 'By Implementing Agency',
		barSize: 22,
		data: impAgencyChartData,
		series: [{ dataKey: 'value', color: '#7A1FE5' }],
		cartesianGrid: { stroke: '#E7EAF3', vertical: false },
		xAxis: { axisLine: false, tickLine: false, interval: 0, tickMargin: 10, height: 40 },
		yAxis: { axisLine: { stroke: '#CBD5E1' }, tickLine: false, width: 52, label: { value: 'Cases', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } } },
		legend: { legend: false },
		handleClick: (e: any) => { const clicked = e?.name; if (clicked) applyFilterFromChart('ia', clicked) },
	}), [impAgencyChartData, applyFilterFromChart])

	const programTypeDonutChartConfig = useMemo(() => ({
		chartType: 'donut',
		dataKey: 'value',
		nameKey: 'name',
		container_width: '100%',
		container_height: 220,
		ariaLabel: 'By Program Type',
		title: 'By Program Type',
		innerRadius: 58,
		outerRadius: 84,
		data: programTypeChartData,
		label: { label: false },
		legend: { legend: false },
		handleClick: (e: any) => { const clicked = e?.name ?? e?.payload?.name; if (clicked) applyFilterFromChart('programtype', clicked) },
	}), [programTypeChartData, applyFilterFromChart])
	return (
		<main id="main-content">
			{loading && <PageOverlay />}
			<RhyBusPageHeader
				pageTitle='Dashboard'
				pageHeaderIcon='icon-rb-dashboard'
				pageHeaderClassName='dashboard-page-header'
				headerRightContent={
					<div className='d-flex align-items-center gap-2 rb-dashboard-filters'>
						<RhyBusFilters
							page='home'
							size='sm'
							formlayout='horizontal'
							config={filterConfig}
							change={filters}
							onFilterChange={handleFilterChange}
							onSearch={handleSearch}
							onClearAll={handleClearAll}
							isCloseButton={true}
							isSearch={true}
							label='Filters'
							labelclassName='filters-sm font-12'
							className='w-100'
                            buttonSize="sm"
                            buttonVariant="text"
                            clearAllIcon="rb-clear"
                            closeVariant="text"
						/>
					</div>
				}
			/>

			<div className='container'>
				<div className='rhybus-kpis-dashboard'>
					<RhyBusDashboardkpis
						handleKpiFilterChange={() => {}}
						KpisData={kpisWithCounts}
					/>
				</div>

				<div className='row rhybus-charts-row mb-3 g-3'>
						<div className='col-12 col-lg-4'>
							<RhyBusCard
								className='h-100'
								isTitleBorder={true}
								cardHeaderLeft={renderPanelHeader('By Country', countryChartData.length > 0 ? countryChartData.reduce((sum, item) => sum + item.value, 0) : undefined)}
								cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => handleViewAll('country')} />{activeChartFilters['country'] && <RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} />}</div>}
							    cardBody={renderChartBody(
								<div className="chart-scroll-wrapper">
									<div style={{ minWidth: Math.max(countryChartData.length * 80 + 60, 200) }}>
										<RhyBusCharts config={countryBarChartConfig as any} />
									</div>
								</div>
							)}
							/>
						</div>
						<div className='col-12 col-lg-4'>
						<RhyBusCard
							className='h-100'
							isTitleBorder={true}
							cardHeaderLeft={renderPanelHeader('By Implementing Agency', impAgencyChartData.length > 0 ? impAgencyChartData.reduce((sum, item) => sum + item.value, 0) : undefined)}
							cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => handleViewAll('ia')} />{activeChartFilters['ia'] && <RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} />}</div>}
							cardBody={renderChartBody(
								<div className="chart-scroll-wrapper">
									<div style={{ minWidth: Math.max(impAgencyChartData.length * 80 + 60, 200) }}>
										<RhyBusCharts config={impAgencyBarChartConfig as any} />
									</div>
								</div>
							)}
						/>
					</div>
					<div className='col-12 col-lg-4'>
							<RhyBusCard
								className='h-100'
								isTitleBorder={true}
								cardHeaderLeft={renderPanelHeader('By Program Type', programTypeChartData.length > 0 ? programTypeChartData.reduce((sum, item) => sum + item.value, 0) : undefined)}
								cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => handleViewAll('programtype')} />{activeChartFilters['programtype'] && <RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} />}</div>}
								cardBody={renderChartBody(<><div className='home-donut-chart'><RhyBusCharts config={programTypeDonutChartConfig as any} /></div>{renderLegend(programTypeChartData)}</>)}
							/>
						</div>						
						{/* <div className='col-12 col-lg-8'>
							<RhyBusCard
								className='h-100'
								isTitleBorder={true}
								cardHeaderLeft={renderPanelHeader('By Monthly Trainings')}
								// cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => navigate('/calendar')} /></div>}
								cardBody={renderChartBody(<div className='row g-3 home-monthly-wrap'><div className='col-12 col-md-8 mt-0'><div className='home-monthly-calendar'><div className='home-monthly-header inter-semibold font-12'>{HOME_NEW_MONTHLY_CALENDAR.label}</div><div className='home-monthly-weekdays'>{HOME_NEW_MONTHLY_CALENDAR.weekdays.map((day) => <div key={day} className='home-monthly-weekday inter-medium font-11'>{day}</div>)}</div><div className='home-monthly-grid'>{HOME_NEW_MONTHLY_CALENDAR.days.map((day, index) => <div key={`${day}-${index}`} className='home-monthly-cell'><span className='home-monthly-day inter-medium font-11'>{day}</span>{HOME_NEW_MONTHLY_CALENDAR.eventDays.includes(Number(day) as 6 | 20) ? <span className='home-monthly-event inter-bold font-8
									 cursor' title='Trainings'>Trainings</span> : null}</div>)}</div></div></div><div className='col-12 col-md-4 mt-0'><div className='home-upcoming-panel'><div className='home-upcoming-title inter-semibold font-12'>Upcoming Trainings</div><div className='home-upcoming-list'>{HOME_NEW_UPCOMING_TRAININGS.map((item) => <div key={item.id} className='home-upcoming-item'><div className='inter-semibold font-10'>APR {item.date}</div><div className='inter-medium font-11'>{item.title}</div><div className='home-upcoming-meta inter-medium font-10'>{item.meta}</div></div>)}</div></div></div></div>)}
							/>
						</div> */}
					<div className='col-12 col-lg-6'>
						<RhyBusCard
							className='h-100'
							isTitleBorder={true}
							cardHeaderLeft={renderPanelHeader('By Grant Util. vs Ceiling')}
							cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => navigate('/cases')} /><RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} /></div>}
							cardBody={<><div className='home-line-legend'><span className='home-line-legend-item'><span className='home-line-legend-dot' style={{ backgroundColor: '#36A953' }} />Utilized</span><span className='home-line-legend-item'><span className='home-line-legend-dot' style={{ backgroundColor: '#C23FC7' }} />Remaining</span></div>{renderChartBody(
								<div className="chart-scroll-wrapper">
									<div className="chart-min-width">
										<RhyBusCharts config={HOME_NEW_GRANT_UTILIZATION_CONFIG as any} />
									</div>
								</div>
							)}</>}
						/>
					</div>

					<div className='col-12 col-lg-6'>
						<RhyBusCard
							className='h-100'
							isTitleBorder={true}
							cardHeaderLeft={renderPanelHeader('By Training Status', trainingStatusChartData.length > 0 ? trainingStatusChartData.reduce((sum, item) => sum + item.value, 0) : undefined)}
							cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => navigate('/trainings')} /></div>}
							cardBody={renderChartBody(
								<div className="chart-scroll-wrapper">
									<div style={{ minWidth: Math.max(trainingStatusChartData.length * 80 + 60, 200) }}>
										<RhyBusCharts config={trainingStatusBarChartConfig as any} />
									</div>
								</div>
							)}
						/>
					</div>
					<div className='col-12 col-lg-4'>
							<RhyBusCard
								className='h-100'
								isTitleBorder={true}
								cardHeaderLeft={renderPanelHeader('By Priority')}
								cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => navigate('/trainings')} /><RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} /></div>}
								cardBody={renderChartBody(<><div className='home-donut-chart'><RhyBusCharts config={HOME_NEW_PRIORITY_DONUT_CONFIG as any} /></div>{renderLegend(HOME_NEW_PRIORITY_DATA)}</>)}
							/>
						</div>
						
						<div className='col-12 col-lg-4'>
							<RhyBusCard
								className='h-100'
								isTitleBorder={true}
								cardHeaderLeft={renderPanelHeader('By Funding Status')}
								cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => navigate('/trainings')} /><RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} /></div>}
								cardBody={renderChartBody(<><div className='home-donut-chart'><RhyBusCharts config={HOME_NEW_FUNDING_STATUS_PIE_CONFIG as any} /></div>{renderLegend(HOME_NEW_FUNDING_STATUS_DATA)}</>)}
							/>
						</div>
						
						<div className='col-12 col-lg-4'>
							<RhyBusCard
								className='h-100'
								isTitleBorder={true}
								cardHeaderLeft={renderPanelHeader('By Top T-MASL Demand')}
								cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => navigate('/trainings')} /><RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} /></div>}
								cardBody={renderChartBody(<RhyBusCharts config={HOME_NEW_TMASL_BAR_CONFIG as any} />)}
							/>
						</div>
						
						{/* <div className='col-12 col-lg-4'>
							<RhyBusCard
								className='h-100'
								isTitleBorder={true}
								cardHeaderLeft={renderPanelHeader('By Seating Status', HOME_NEW_DISPLAY.taskStatusCount)}
								cardHeaderRight={<div className='d-flex gap-2 align-items-center'><RhyBusButton variant='secondary' label='View All' size='md' onClick={() => navigate('/trainings')} /><RhyBusButton variant='secondary' label='Reset Filter' size='md' onClick={handleClearAll} /></div>}
								cardBody={renderChartBody(<><div className='home-donut-chart'><RhyBusCharts config={HOME_NEW_TASK_STATUS_DONUT_CONFIG} /><div className='home-donut-center'><span className='home-donut-center-label inter-medium font-12'>Total</span><span className='home-donut-center-value inter-semibold font-20'>{HOME_NEW_DISPLAY.taskStatusTotal}</span></div></div>{renderLegend(HOME_NEW_TASK_STATUS_DATA)}</>)}
							/>
						</div> */}

						<div className='col-12 col-lg-4'>
							<RhyBusCalendarDashboard
							 componentTitle='Event Calendar'
							 viewAllLink='/eventcalendar'
							 viewAllLabel='View All'
							 calendarCategories={homeCalendarCategories}
							 calendarEvents={dashboardCalendarEvents as any[]} />
						
						</div>

                        <div className="col-12 col-lg-4">
                           <RhyBusQuickLinksDashBoard items={quickLinksItems} maxItems={8} viewAllLabel='View All' viewAllUrl='/quick-links' page='home' />                        
                        </div>
						<div className='col-12 col-lg-4'>
							<RhyBusPointofContactDashBoard
								pocData={pocData as any[]}
								pocIconClassName='icon-rb-poc font-30'
								componentTitle='Points of Contact'
								viewAllLink='/points-of-contact'
								viewAllLabel='View All'
								maxDisplay={3}
								showArchivedItems={false}
								cardSize='lg'
							/>
						</div>
					</div>
			</div>
		</main>
	)
}
